import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight, ArrowLeft, Save, AlertCircle, ChevronUp, ChevronDown } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../common_components/Toast";
import Loader from "../../../common_components/Loader";
import DynamicSection from "../../../modules/dynamic-form/components/DynamicSection";
import { validateForm, calculateExpectedFee } from "../../../modules/dynamic-form/validators/validators";
import { AccountDetailsCard } from "../../../common_components/Account";
import { ALLIED_DEFAULT_SCHEMA } from "../../../modules/dynamic-form/constants/presets";
import { ApplicationStatus, PaymentStatus, matchStatus } from "../../../types/status";
import { getCourseDisplayName } from "../constants/courses";

interface AccountDetails {
  bankName: string;
  branchName: string;
  accountNumber: string;
  ifsc: string;
  qrCodeKey?: string;
  qrCodeUrl?: string;
}

interface Step {
  id: number;
  name: string;
  sectionId: string;
}

interface PaymentState {
  status: string;
  utr: string;
  date: string;
}

interface DeclarationState {
  accepted: boolean;
  acceptedAt: string | null;
}

interface Course {
  name?: string;
}

interface Vacancy {
  title?: string;
  applicationFormSchema?: {
    sections: Array<{ id: string; title: string;[key: string]: any }>;
  };
  paymentDetails?: {
    amount?: Record<string, number>;
  };
  courses?: Course[];
  applicationDates?: {
    startDate?: string;
    endDate?: string;
  };
}

export default function AlliedApplicationForm() {
  const { admissionId, applicationId: urlApplicationId } = useParams<{
    admissionId: string;
    applicationId?: string;
  }>();
  const navigate = useNavigate();
  const { isStudentAdmin, isSuperAdmin, apiRequest } = useAuth();
  const isAdmin = isStudentAdmin || isSuperAdmin;
  const toast = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applicationId, setApplicationId] = useState<string | null>(urlApplicationId ?? null);
  const [vacancy, setVacancy] = useState<Vacancy | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Form State
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [preferenceOrder, setPreferenceOrder] = useState<string[]>([]);
  const [payment, setPayment] = useState<PaymentState>({ status: PaymentStatus.PENDING, utr: "", date: "" });
  const [declaration, setDeclaration] = useState<DeclarationState>({ accepted: false, acceptedAt: null });
  const [accountDetails, setAccountDetails] = useState<AccountDetails>({ bankName: "", branchName: "", accountNumber: "", ifsc: "" });

  const dateStatus = (() => {
    if (!vacancy?.applicationDates) return { canApply: true, message: null, isAdminTesting: false };
    const now = new Date();
    const startDate = vacancy.applicationDates.startDate ? new Date(vacancy.applicationDates.startDate) : null;
    const endDate = vacancy.applicationDates.endDate ? new Date(vacancy.applicationDates.endDate) : null;

    const isUpcoming = startDate ? now < startDate : false;
    if (isUpcoming && startDate && !isAdmin) {
      const formattedStart = startDate.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
      return {
        canApply: false,
        message: `Applications for this admission have not started yet. Applications open on ${formattedStart}.`,
        isAdminTesting: false,
      };
    }
    if (endDate && now > endDate) {
      const formattedEnd = endDate.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
      return {
        canApply: false,
        message: `Application period for this admission closed on ${formattedEnd}.`,
        isAdminTesting: false,
      };
    }
    return { canApply: true, message: null, isAdminTesting: isUpcoming && isAdmin };
  })();

  // Fetch Vacancy
  useEffect(() => {
    const fetchVacancy = async () => {
      try {
        const res = await apiRequest(`/admission-vacancies/${admissionId}`);
        if (res && res.data) {
          const schema =
            res.data.applicationFormSchema && res.data.applicationFormSchema.sections
              ? res.data.applicationFormSchema
              : ALLIED_DEFAULT_SCHEMA;

          const vacancyData: Vacancy = { ...res.data, applicationFormSchema: schema };
          setVacancy(vacancyData);

          const sections = schema.sections || [];
          const dynamicSteps: Step[] = sections.map((sec: { id: string; title: string }, idx: number) => ({
            id: idx + 1,
            name: sec.title,
            sectionId: sec.id,
          }));
          dynamicSteps.push({ id: sections.length + 1, name: "Review & Submit", sectionId: "review" });
          setSteps(dynamicSteps);

          if (!urlApplicationId) {
            const apps = await apiRequest("/applications/my-applications");
            const existingDraft = apps?.find(
              (app: any) => app.admissionId === admissionId && (matchStatus(app.status, ApplicationStatus.DRAFT) || matchStatus(app.status, ApplicationStatus.DRAFT))
            );
            if (existingDraft) {
              setApplicationId(existingDraft.applicationId);
              return;
            }

            const initialForm: Record<string, any> = {};
            sections.forEach((sec: { id: string }) => { initialForm[sec.id] = {}; });
            setFormData(initialForm);
          }
        }
      } catch (e) {
        toast.error("Failed to load admission details.");
      } finally {
        if (!urlApplicationId) setLoading(false);
      }
    };
    fetchVacancy();
  }, [admissionId, apiRequest, toast, urlApplicationId]);

  // Fetch Account Details for payment instructions
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const data = await apiRequest(`/account`, { method: "GET" });
        setAccountDetails(data);
      } catch (err) {
        console.error("Error fetching account details:", err);
      }
    };
    fetchAccountDetails();
  }, [apiRequest]);

  const fetchedRef = React.useRef(false);

  // Redirect if draft is generated
  useEffect(() => {
    if (applicationId && !urlApplicationId) {
      navigate(`/allied/admission/${admissionId}/apply/${applicationId}`, { replace: true });
    }
  }, [applicationId, admissionId, navigate, urlApplicationId]);

  const handleFetchApplication = useCallback(async () => {
    try {
      const res = await apiRequest(`/applications/${urlApplicationId}`);
      if (res) {
        let loadedFormData: Record<string, any> = res.formData || {};

        // Reconstruct nested formData from flat fields if missing (backward compatibility)
        if (Object.keys(loadedFormData).length === 0) {
          loadedFormData = {
            personalInfo: {
              name: res.candidateName || "",
              dateOfBirth: res.dob ? res.dob.split("T")[0] : "",
              gender: res.gender === "M" ? "Male" : res.gender === "F" ? "Female" : "Transgender",
              categoryOfCandidate: res.category || "",
              fathersName: res.fathersName || "",
              email: res.email || "",
              mobile: res.mobile || "",
              alternateMobile: res.alternateMobile || "",
              physicalIdentification: res.physicalIdentification || "",
              isPWD: !!res.pwd,
              sameAddress: !!res.addressSameAsPermanent,
              currentAddress: res.correspondAddress
                ? { addressLine: res.correspondAddress.address || "", city: res.correspondAddress.district || "", state: res.correspondAddress.state || "", pincode: res.correspondAddress.pincode || "" }
                : { addressLine: "", city: "", state: "", pincode: "" },
              permanentAddress: res.permanentAddress
                ? { addressLine: res.permanentAddress.address || "", city: res.permanentAddress.district || "", state: res.permanentAddress.state || "", pincode: res.permanentAddress.pincode || "" }
                : { addressLine: "", city: "", state: "", pincode: "" },
            },
            neetDetails: {
              neetRoll: res.neetRoll || "",
              neetAIRank: res.neetAIRank || "",
              neetCategoryRank: res.neetCategoryRank || "",
            },
            education: {
              board: res.intermediateSchool?.board || "",
              passingYear: res.intermediateSchool?.passingYear || "",
              school: res.intermediateSchool?.school || "",
              schoolState: res.intermediateSchool?.state || "",
              obtainedMarks: res.intermediateSchool?.obtainedMarks || "",
              totalMarks: res.intermediateSchool?.totalMarks || "",
              percentage: res.intermediateSchool?.percentage || "",
              physics: !!res.physics,
              chemistry: !!res.chemistry,
              biology: !!res.biology,
              english: !!res.english,
            },
            documents: res.documents || {},
          };
        }

        setFormData(loadedFormData);
        setPreferenceOrder(res.preferenceOrder || []);
        setPayment(res.payment || { status: PaymentStatus.PENDING, utr: "", date: "" });
        setApplicationId(res.applicationId);

        if (!matchStatus(res.status, ApplicationStatus.DRAFT) && !matchStatus(res.status, ApplicationStatus.DRAFT)) setIsSubmitted(true);
      }
    } catch (error) {
      toast.error("Failed to fetch application details.");
    } finally {
      setLoading(false);
    }
  }, [urlApplicationId, apiRequest, toast]);

  // Load existing application
  useEffect(() => {
    if (urlApplicationId && !fetchedRef.current) {
      fetchedRef.current = true;
      setApplicationId(urlApplicationId);
      handleFetchApplication();
    }
  }, [urlApplicationId, handleFetchApplication]);

  const handleFieldChange = (sectionId: string, fieldId: string, value: any) => {
    setFormData((prev) => {
      const updatedSection: Record<string, any> = { ...(prev[sectionId] as Record<string, any> || {}), [fieldId]: value };

      // Calculate percentage if education marks change
      if (sectionId === "education") {
        const obtained = fieldId === "obtainedMarks" ? Number(value) : Number(updatedSection.obtainedMarks || 0);
        const total = fieldId === "totalMarks" ? Number(value) : Number(updatedSection.totalMarks || 0);
        if (total > 0 && obtained >= 0) {
          updatedSection.percentage = Number(((obtained / total) * 100).toFixed(2));
        } else {
          updatedSection.percentage = 0;
        }
      }

      // Address mirroring
      if (sectionId === "personalInfo" && fieldId === "sameAddress") {
        if (value) updatedSection.permanentAddress = { ...(updatedSection.currentAddress as Record<string, any> || {}) };
      }
      if (sectionId === "personalInfo" && fieldId === "currentAddress" && updatedSection.sameAddress) {
        updatedSection.permanentAddress = { ...(value as Record<string, any>) };
      }

      return { ...prev, [sectionId]: updatedSection };
    });
  };

  const validateCurrentState = (stepIndex: number, customFormData: Record<string, any> | null = null): boolean => {
    if (!vacancy?.applicationFormSchema) return true;
    const sectionsToValidate = vacancy.applicationFormSchema.sections.slice(0, stepIndex);
    const subSchema = { sections: sectionsToValidate };
    const dataToValidate = customFormData || formData;
    const errors = validateForm(subSchema, dataToValidate, vacancy);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveProgress = async (nextStep: number = currentStep, customFormData: Record<string, any> | null = null): Promise<boolean> => {
    if (isSubmitted) return true;

    setSaving(true);
    try {
      const activeData = customFormData || formData;
      const personalInfo = activeData.personalInfo as Record<string, any> | undefined;

      // Sanitize fields
      if (personalInfo && personalInfo.alternateMobile === "") {
        personalInfo.alternateMobile = null;
      }

      const payload = {
        admissionId,
        formData: activeData,
        preferenceOrder,
        payment: { ...payment, status: payment.status || PaymentStatus.PENDING },
        addressSameAsPermanent: !!(personalInfo?.sameAddress),
        status: ApplicationStatus.DRAFT,
      };

      const method = applicationId ? "PUT" : "POST";
      const url = applicationId ? `/applications/${applicationId}` : `/applications`;

      const res = await apiRequest(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res && res.applicationId) {
        setApplicationId(res.applicationId);
        if (!urlApplicationId) {
          navigate(`/allied/admission/${admissionId}/apply/${res.applicationId}`, { replace: true });
        }
      }

      toast.success("Progress saved successfully!");
      return true;
    } catch (err: any) {
      const e = err as { message?: string };
      toast.error(e.message || "Failed to save draft progress.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (isSubmitted) return;

    if (!declaration.accepted) {
      toast.error("You must accept the declaration to submit.");
      return;
    }

    const sectionsCount = vacancy?.applicationFormSchema?.sections?.length || 0;
    const totalValid = validateCurrentState(sectionsCount, formData);
    if (!totalValid) {
      toast.error("Please complete all sections accurately before submission.");
      return;
    }

    if (!preferenceOrder || preferenceOrder.length === 0) {
      toast.error("Please select at least one course preference.");
      return;
    }

    const pInfo = (formData?.personalInfo || {}) as Record<string, any>;
    let expectedFee = calculateExpectedFee(formData, vacancy);

    if (expectedFee > 0) {
      if (!payment.utr || !payment.date) {
        toast.error("UTR/Transaction ID and payment date are required.");
        return;
      }
      const documents = (formData.documents || {}) as Record<string, { key?: string }>;
      if (!documents.paymentCert?.key) {
        toast.error("Please upload payment receipt screenshot.");
        return;
      }
    }

    setSaving(true);
    try {
      const finalPayment = { ...payment, status: expectedFee > 0 ? PaymentStatus.PAID : PaymentStatus.PENDING };

      const payload = {
        admissionId,
        formData,
        preferenceOrder,
        payment: finalPayment,
        addressSameAsPermanent: !!(pInfo.sameAddress),
        status: ApplicationStatus.SUBMITTED,
      };

      await apiRequest(`/applications/${applicationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      toast.success("Application submitted successfully!");
      setIsSubmitted(true);
      navigate(`/allied/applications/all`);
    } catch (err: any) {
      const e = err as { message?: string };
      toast.error(e.message || "Failed to submit application.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  const expectedFee = calculateExpectedFee(formData, vacancy);

  const currentSections = vacancy?.applicationFormSchema?.sections;
  const activeSection = currentSections?.[currentStep - 1];

  return (
    <div className="flex-grow bg-slate-50 p-4 py-12 flex justify-center w-full min-h-screen">
      <div className="w-full max-w-4xl space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{vacancy?.title}</h1>
            <p className="text-sm text-slate-500 mt-1">Application ID: {applicationId || "Draft Generation Pending"}</p>
          </div>
          <div className="flex gap-2">
            {!isSubmitted && (
              <button
                onClick={() => saveProgress(currentStep)}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 border border-slate-300 text-slate-700 bg-white hover:bg-slate-100 rounded-lg text-sm font-semibold transition-colors shadow-sm"
              >
                <Save className="w-4 h-4" /> Save Draft
              </button>
            )}
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm overflow-x-auto">
          <div className="flex items-center justify-between min-w-[600px] md:min-w-0">
            {steps.map((st, sIdx) => {
              const isActive = currentStep === st.id;
              const isDone = currentStep > st.id;

              return (
                <React.Fragment key={st.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${isDone
                        ? "bg-green-600 border-green-600 text-white"
                        : isActive
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-400"
                        }`}
                    >
                      {isDone ? <CheckCircle className="w-5 h-5" /> : <span>{st.id}</span>}
                    </div>
                    <span className={`text-[10px] md:text-xs font-semibold mt-2 ${isActive ? "text-blue-600" : "text-gray-500"}`}>
                      {st.name}
                    </span>
                  </div>
                  {sIdx < steps.length - 1 && (
                    <div className={`h-[2px] flex-1 mx-2 ${currentStep > st.id ? "bg-green-500" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form Body */}
        <div className="space-y-6">
          {dateStatus.isAdminTesting && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl flex items-start gap-3 text-blue-900 shadow-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-600" />
              <div>
                <h3 className="font-bold text-blue-950">Admin Testing Mode</h3>
                <p className="text-sm text-blue-900 mt-0.5">
                  Applications for this admission have not officially started yet. As an Admin, you are allowed to fill and test this application form before the start date.
                </p>
              </div>
            </div>
          )}

          {!dateStatus.canApply && (
            <div className="p-4 bg-amber-50 border border-amber-300 rounded-2xl flex items-start gap-3 text-amber-900 shadow-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <h3 className="font-bold text-amber-950">Application Window Inactive</h3>
                <p className="text-sm text-amber-900 mt-0.5">{dateStatus.message}</p>
              </div>
            </div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl space-y-2 text-sm text-red-800 shadow-xs">
              <div className="flex gap-2 font-bold items-center">
                <AlertCircle className="w-5 h-5 flex-shrink-0" stroke="currentColor" />
                <span>Please correct the following errors before proceeding:</span>
              </div>
              <ul className="list-disc list-inside text-xs text-red-700 pl-6 space-y-1">
                {Object.entries(validationErrors).map(([key, errVal]) => {
                  if (typeof errVal === "string") {
                    return <li key={key}>{errVal}</li>;
                  }
                  if (typeof errVal === "object" && errVal !== null) {
                    return Object.entries(errVal).map(([subKey, subErr]) => {
                      if (typeof subErr === "string") {
                        return <li key={`${key}.${subKey}`}>{subErr}</li>;
                      }
                      if (typeof subErr === "object" && subErr !== null) {
                        return Object.entries(subErr).map(([k, e]) => (
                          <li key={`${key}.${subKey}.${k}`}>{String(e)}</li>
                        ));
                      }
                      return null;
                    });
                  }
                  return null;
                })}
              </ul>
            </div>
          )}

          {currentStep <= (currentSections?.length || 0) ? (
            <div className="space-y-6">
              {activeSection?.id === "documents" && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
                  <div className="border-b pb-3">
                    <h3 className="text-xl font-bold text-slate-900">Application Fee & Payment Details</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Please scan the QR code or transfer the registration fee to the bank account below, enter your transaction reference details, and then upload your payment receipt below.
                    </p>
                  </div>

                  {expectedFee > 0 ? (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
                        <span className="text-md font-bold text-blue-900">Required Registration Fee:</span>
                        <span className="text-2xl font-black text-blue-950">₹{expectedFee}</span>
                      </div>

                      <AccountDetailsCard accountDetails={accountDetails} />

                      <div className="grid md:grid-cols-2 gap-4 pt-2">
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700">
                            UTR / Transaction ID <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={payment.utr || ""}
                            onChange={(e) => setPayment((prev) => ({ ...prev, utr: e.target.value }))}
                            disabled={isSubmitted || !dateStatus.canApply}
                            placeholder="Enter bank transaction reference number"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700">
                            Payment Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={payment.date ? payment.date.substring(0, 10) : ""}
                            onChange={(e) => setPayment((prev) => ({ ...prev, date: e.target.value }))}
                            disabled={isSubmitted || !dateStatus.canApply}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between text-green-800">
                      <span className="text-sm font-bold">Fee Exemption Applied: ₹0 Registration Fee required for your category.</span>
                    </div>
                  )}
                </div>
              )}

              {activeSection && (
                <DynamicSection
                  section={activeSection}
                  sectionData={(formData[activeSection.id] as Record<string, any>) || {}}
                  onChange={handleFieldChange}
                  errors={validationErrors}
                  applicationId={applicationId || ""}
                  disabled={isSubmitted || !dateStatus.canApply}
                  folder="allied"
                  recruitment={vacancy}
                />
              )}
            </div>
          ) : (
            <div className="space-y-8">
              {/* Dynamic Review Sections */}
              {vacancy?.applicationFormSchema?.sections?.map((sec) => (
                <DynamicSection
                  key={sec.id}
                  section={sec}
                  sectionData={(formData[sec.id] as Record<string, any>) || {}}
                  onChange={() => { }}
                  errors={{}}
                  applicationId={applicationId || ""}
                  disabled={true}
                  folder="allied"
                  recruitment={vacancy}
                />
              ))}

              {/* Course Preference Selection */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b pb-3 gap-2">
                  <div>
                    <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                      Course Preference Order *
                      <span className="text-xs bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full font-semibold">
                        {preferenceOrder.length} of {vacancy?.courses?.length || 0} Selected
                      </span>
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Click to select courses, then use the arrows below to reorder by preference rank.
                    </p>
                  </div>
                  {!isSubmitted && vacancy?.courses && vacancy.courses.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const allCourseNames = vacancy.courses?.map((c) => c.name || "").filter(Boolean) || [];
                          setPreferenceOrder(allCourseNames);
                        }}
                        className="text-xs font-semibold px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                      >
                        Select All
                      </button>
                      {preferenceOrder.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setPreferenceOrder([])}
                          className="text-xs font-semibold px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-250 rounded-lg transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  {/* Grid of course option buttons */}
                  <div className="flex flex-wrap gap-3">
                    {vacancy?.courses?.map((course) => {
                      const courseName = course?.name || "";
                      const prefIndex = preferenceOrder.indexOf(courseName);
                      const isSelected = prefIndex !== -1;
                      return (
                        <button
                          key={courseName}
                          type="button"
                          disabled={isSubmitted}
                          onClick={() => {
                            if (isSelected) {
                              setPreferenceOrder(preferenceOrder.filter((c) => c !== courseName));
                            } else {
                              setPreferenceOrder([...preferenceOrder, courseName]);
                            }
                          }}
                          className={`flex items-center gap-2.5 px-4 py-3 border rounded-xl cursor-pointer transition-all ${isSelected
                            ? "border-blue-500 bg-blue-50/70 text-blue-950 font-bold shadow-xs"
                            : "border-slate-250 bg-white text-slate-700 hover:border-blue-300 hover:bg-slate-50 font-medium"
                            }`}
                        >
                          <span className="text-sm leading-snug">{getCourseDisplayName(courseName)}</span>
                          {isSelected && (
                            <span className="bg-blue-600 text-white text-xs font-black px-2 py-0.5 rounded-full min-w-[22px] text-center shadow-xs">
                              #{prefIndex + 1}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected Preference Order List with re-order buttons */}
                  {preferenceOrder.length > 0 && (
                    <div className="mt-2 p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                      <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center justify-between">
                        <span>Selected Preference Ranking ({preferenceOrder.length} Selected)</span>
                        <span className="text-[11px] text-slate-400 font-normal">Use arrows to change rank order</span>
                      </h4>
                      <div className="space-y-2">
                        {preferenceOrder.map((courseName, idx) => (
                          <div
                            key={courseName}
                            className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-2xs"
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-black flex items-center justify-center">
                                {idx + 1}
                              </span>
                              <span className="text-sm font-bold text-slate-800">
                                {getCourseDisplayName(courseName)}
                              </span>
                            </div>

                            {!isSubmitted && (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  disabled={idx === 0}
                                  onClick={() => {
                                    const next = [...preferenceOrder];
                                    const temp = next[idx];
                                    next[idx] = next[idx - 1];
                                    next[idx - 1] = temp;
                                    setPreferenceOrder(next);
                                  }}
                                  title="Move Up"
                                  className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:pointer-events-none"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  disabled={idx === preferenceOrder.length - 1}
                                  onClick={() => {
                                    const next = [...preferenceOrder];
                                    const temp = next[idx];
                                    next[idx] = next[idx + 1];
                                    next[idx + 1] = temp;
                                    setPreferenceOrder(next);
                                  }}
                                  title="Move Down"
                                  className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:pointer-events-none"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Verification */}
              {expectedFee > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
                  <div className="border-b pb-3">
                    <h3 className="text-xl font-bold text-slate-900">Payment Verification</h3>
                    <p className="text-sm text-slate-500 mt-1">This application requires a registration fee payment.</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
                    <span className="text-md font-bold text-blue-900">Required Registration Fee:</span>
                    <span className="text-2xl font-black text-blue-950">₹{expectedFee}</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-gray-700">UTR / Transaction ID *</label>
                      <input
                        type="text"
                        value={payment.utr || ""}
                        onChange={(e) => setPayment((prev) => ({ ...prev, utr: e.target.value }))}
                        disabled={isSubmitted}
                        placeholder="Enter bank transaction reference number"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-gray-700">Payment Date *</label>
                      <input
                        type="date"
                        value={payment.date ? payment.date.substring(0, 10) : ""}
                        onChange={(e) => setPayment((prev) => ({ ...prev, date: e.target.value }))}
                        disabled={isSubmitted}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Declaration by Candidate */}
              <div className="bg-amber-50/50 border border-amber-200 rounded-2xl p-6 space-y-4">
                <h3 className="text-lg font-bold text-amber-900">Declaration by Candidate</h3>
                <p className="text-sm text-amber-950 leading-relaxed font-medium">
                  I hereby declare that the above information is true, complete and correct to the best of my knowledge and belief.
                  I have not suppressed any material, fact or factual information. I understand that my candidature is liable to be
                  rejected in the event of any misstatement/discrepancy being detected before or after my admission.
                </p>
                <label className="flex items-start gap-3 cursor-pointer pt-2 select-none">
                  <input
                    type="checkbox"
                    checked={declaration.accepted}
                    disabled={isSubmitted}
                    onChange={(e) => setDeclaration((prev) => ({ ...prev, accepted: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mt-0.5"
                  />
                  <span className="text-sm font-bold text-amber-900">
                    I accept the above declaration and confirm that all information provided is accurate.
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Navigation Controls */}
          <div className="flex items-center justify-between pt-6 border-t border-slate-200 bg-white p-5 rounded-2xl border shadow-sm">
            <button
              type="button"
              onClick={() => {
                if (currentStep > 1) {
                  setCurrentStep(currentStep - 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  navigate("/allied");
                }
              }}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 border border-slate-350 text-slate-700 bg-white hover:bg-slate-100 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" /> {currentStep === 1 ? "Exit" : "Previous"}
            </button>

            <div className="flex gap-3">
              {!isSubmitted && (
                <button
                  type="button"
                  onClick={() => saveProgress(currentStep)}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 border border-slate-350 text-slate-750 bg-white hover:bg-slate-100 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
                >
                  <Save className="w-4 h-4" /> Save Draft
                </button>
              )}

              {currentStep <= (currentSections?.length || 0) ? (
                <button
                  type="button"
                  onClick={async () => {
                    const isValid = validateCurrentState(currentStep, formData);
                    if (!isValid) {
                      toast.error("Please resolve validation errors before proceeding.");
                      return;
                    }
                    if (activeSection?.id === "documents" && expectedFee > 0) {
                      if (!payment.utr || !payment.date) {
                        toast.error("Please enter UTR / Transaction ID and Payment Date.");
                        return;
                      }
                      const docs = (formData.documents || {}) as Record<string, { key?: string }>;
                      if (!docs.paymentCert?.key) {
                        toast.error("Please upload payment receipt screenshot.");
                        return;
                      }
                    }
                    const saved = await saveProgress(currentStep + 1, formData);
                    if (saved) {
                      setCurrentStep(currentStep + 1);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
                >
                  Next Step <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={saving || isSubmitted || !declaration.accepted || !dateStatus.canApply}
                  className="flex items-center gap-1.5 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Submitting..." : "Submit Application"} <CheckCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}